/**
 * VIDÉOS TECHNIQUES — public/membre /videos (univers DARK premium KAIZEN)
 * - Visiteur : catalogue public jouable + capsules verrouillées (teasers).
 * - Membre connecté (member_token) : bibliothèque complète + gamification
 *   (crédits, badges) ; capsules premium verrouillées si non-premium.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Play, Lock, ArrowLeft, Trophy, Coins, Crown, X, CheckCircle, Eye, Sparkle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_WHITE = "/logos/master_text_sm.webp";

const ACCESS_TAG = {
  public: { label: "Gratuit", cls: "bg-emerald-500/15 text-emerald-300" },
  membres: { label: "Membres", cls: "bg-blue-500/15 text-blue-300" },
  premium: { label: "Premium", cls: "bg-amber-500/15 text-amber-300" },
};

export default function VideosTechPublic() {
  const [data, setData] = useState(null);
  const [isMember, setIsMember] = useState(false);
  const [progress, setProgress] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [discipline, setDiscipline] = useState("");
  const [videoType, setVideoType] = useState("all"); // all | technique | combo
  const [search, setSearch] = useState("");
  const [groupByBelt, setGroupByBelt] = useState(true);
  const [sortBy, setSortBy] = useState("default"); // default | popular
  const [durationBucket, setDurationBucket] = useState("all"); // all | 10 | 15 | 30 | 45 | 60

  const token = typeof window !== "undefined" ? localStorage.getItem("member_token") : null;

  const load = useCallback(async () => {
    const params = discipline ? `?discipline=${encodeURIComponent(discipline)}` : "";
    try {
      if (token) {
        const r = await axios.get(`${API}/videos/member/library${params}`, { headers: { Authorization: `Bearer ${token}` } });
        setData(r.data); setIsMember(true); setProgress(r.data.progress); setIsPremium(r.data.is_premium);
      } else {
        const r = await axios.get(`${API}/videos/public${params}`);
        setData(r.data); setIsMember(false);
      }
    } catch {
      const r = await axios.get(`${API}/videos/public${params}`);
      setData(r.data); setIsMember(false);
    }
  }, [discipline, token]);

  useEffect(() => { load(); }, [load]);

  // Retour du paiement premium Stripe → vérifie le statut et active
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("premium") === "success" && sp.get("session_id") && token) {
      (async () => {
        try {
          const r = await axios.get(`${API}/videos/member/premium/status/${sp.get("session_id")}`, { headers: { Authorization: `Bearer ${token}` } });
          if (r.data.premium) { toast.success("🎉 Bienvenue dans le Premium ! Capsules avancées débloquées."); load(); }
          else toast("Paiement en cours de confirmation…");
        } catch { /* ignore */ }
        window.history.replaceState({}, "", "/videos");
      })();
    }
    if (sp.get("combo") === "success" && sp.get("session_id") && token) {
      (async () => {
        try {
          const r = await axios.get(`${API}/videos/member/combo-pack/status/${sp.get("session_id")}`, { headers: { Authorization: `Bearer ${token}` } });
          if (r.data.combo_pack) { toast.success("🥋 Volet Combinaisons débloqué ! Enchaînements accessibles."); load(); }
          else toast("Paiement en cours de confirmation…");
        } catch { /* ignore */ }
        window.history.replaceState({}, "", "/videos");
      })();
    }
  }, [token, load]);

  async function goPremium() {
    if (!token) return;
    try {
      const r = await axios.post(`${API}/videos/member/premium/checkout`, { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  }

  async function goComboPack() {
    if (!token) { toast("Connecte-toi comme membre pour débloquer les Combinaisons."); return; }
    try {
      const r = await axios.post(`${API}/videos/member/combo-pack/checkout`, { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  }

  async function markWatched(v) {
    if (!token) return;
    try {
      const r = await axios.post(`${API}/videos/member/${v.id}/watch`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (r.data.already) { toast("Déjà complétée"); }
      else {
        toast.success(`+${r.data.awarded_credits} crédits${r.data.new_badge ? ` · Badge « ${r.data.new_badge} » !` : ""}`);
      }
      setProgress({ credits: r.data.credits, badges: r.data.badges, watched_count: (progress?.watched_count || 0) + (r.data.already ? 0 : 1) });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  }

  async function openPlayer(v) {
    if (v.locked) return;
    setPlaying(v);
    // Compteur de vues (best-effort, sans bloquer la lecture)
    try {
      await axios.post(`${API}/videos/public/${v.id}/view`);
      setData((d) => d && ({ ...d, videos: d.videos.map((x) => x.id === v.id ? { ...x, views: (x.views || 0) + 1 } : x) }));
    } catch { /* ignore */ }
  }

  const videos = data?.videos || [];
  const disciplines = data?.disciplines || [];
  const voletOf = (v) => v.volet || (v.is_combo ? "combo" : "technique");
  const comboCount = videos.filter((v) => voletOf(v) === "combo").length;
  const trainingCount = videos.filter((v) => voletOf(v) === "training").length;
  const techniqueCount = videos.filter((v) => voletOf(v) === "technique").length;
  const shownVideosRaw = videos.filter((v) => videoType === "all" ? true : voletOf(v) === videoType);
  // Filtre par durée (volet Training uniquement) : « j'ai X minutes »
  const inBucket = (v) => {
    if (durationBucket === "all") return true;
    const d = Number(v.duration_min) || 0;
    if (durationBucket === "60") return d >= 46; // 60+ min (long)
    return d > 0 && d <= Number(durationBucket);
  };
  const shownVideosFiltered = videoType === "training" ? shownVideosRaw.filter(inBucket) : shownVideosRaw;
  const q = (search || "").trim().toLowerCase();
  const shownVideosSearched = q
    ? shownVideosFiltered.filter((v) => [v.title, v.description, v.discipline, v.belt_level, ...(v.tags || [])]
        .filter(Boolean).join(" ").toLowerCase().includes(q))
    : shownVideosFiltered;
  const byViews = (a, b) => (b.views || 0) - (a.views || 0);
  const shownVideos = sortBy === "popular" ? [...shownVideosSearched].sort(byViews) : shownVideosSearched;

  const beltRank = (b) => {
    const s = (b || "").toLowerCase();
    const order = ["blanc", "jaune", "orange", "vert", "bleu", "violet", "pourpre", "marron", "brun", "noir"];
    const idx = order.findIndex((c) => s.includes(c));
    return idx === -1 ? 99 : idx;
  };
  const beltGroups = (() => {
    const map = new Map();
    for (const v of shownVideos) {
      const key = v.belt_level || "Sans ceinture";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(v);
    }
    const entries = [...map.entries()];
    if (sortBy === "popular") {
      const total = (vids) => vids.reduce((s, x) => s + (x.views || 0), 0);
      return entries.sort((a, b) => total(b[1]) - total(a[1]));
    }
    return entries.sort((a, b) => beltRank(a[0]) - beltRank(b[0]) || a[0].localeCompare(b[0]));
  })();

  const renderCard = (v) => {
    const tag = ACCESS_TAG[v.access_level] || ACCESS_TAG.public;
    return (
      <div key={v.id} data-testid={`video-card-${v.id}`}
        className={`border border-white/10 bg-white/[0.02] overflow-hidden group ${v.locked ? "" : "hover:border-white/25 cursor-pointer"} transition-colors`}
        onClick={() => openPlayer(v)}>
        <div className="aspect-video bg-zinc-900 relative flex items-center justify-center">
          {v.thumbnail_url ? <img src={v.thumbnail_url} alt={v.title} loading="lazy" referrerPolicy="no-referrer" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full h-full object-cover" /> : <Play size={40} className="text-white/20" />}
          {v.is_new && !v.locked && (
            <span className="absolute top-2 left-2 flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 bg-[#D90429] text-white rounded-sm" data-testid={`new-badge-${v.id}`}>
              <Sparkle size={10} weight="fill" /> Nouveau
            </span>
          )}
          {v.locked ? (
            <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-2" data-testid={`locked-${v.id}`}>
              <Lock size={26} className="text-amber-300" />
              <span className="kz-mono text-[10px] uppercase tracking-widest text-amber-300">{v.access_level === "premium" ? "Premium" : "Membres"}</span>
            </div>
          ) : (
            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"><Play size={44} weight="fill" className="text-white" /></div>
          )}
          {v.watched && <span className="absolute top-2 right-2 text-emerald-400" data-testid={`watched-${v.id}`}><CheckCircle size={20} weight="fill" /></span>}
        </div>
        <div className="p-4">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 ${tag.cls}`}>{tag.label}</span>
            {voletOf(v) === "combo" && <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 bg-amber-400/15 text-amber-300" data-testid={`combo-badge-${v.id}`}>⚡ Enchaînement</span>}
            {voletOf(v) === "training" && <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 bg-sky-400/15 text-sky-300" data-testid={`training-badge-${v.id}`}>🎬 Training</span>}
            {v.discipline && <span className="text-[9px] uppercase tracking-wide text-white/40">{v.discipline}</span>}
          </div>
          <h3 className="kz-display text-lg leading-tight">{v.title}</h3>
          {v.description && <p className="kz-body text-xs text-white/50 mt-1.5 line-clamp-2">{v.description}</p>}
          <div className="flex items-center gap-3 mt-3 text-[11px] text-white/40">
            {v.belt_level && <span>{v.belt_level}</span>}
            {v.duration_min && <span>{v.duration_min} min</span>}
            <span className="flex items-center gap-1" data-testid={`views-${v.id}`}><Eye size={12} />{v.views || 0} vue{(v.views || 0) > 1 ? "s" : ""}</span>
            {v.bunny_views > 0 && <span className="flex items-center gap-1 text-white/30" data-testid={`bunny-views-${v.id}`} title="Lectures réelles côté Bunny (CDN)"><Play size={11} weight="fill" />{v.bunny_views} Bunny</span>}
            <span className="text-amber-300 flex items-center gap-1"><Coins size={12} />{v.credits_reward} cr.</span>
          </div>
          {v.locked && v.access_level === "premium" && !isPremium && (
            <p className="text-[11px] text-amber-300/80 mt-2">Réservé aux membres premium.</p>
          )}
          {v.locked && v.access_level === "membres" && !isMember && (
            <Link to="/membres" className="text-[11px] text-[#D90429] mt-2 inline-block" data-testid={`unlock-${v.id}`}>Connecte-toi pour débloquer →</Link>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="kz-body min-h-screen bg-[#0A0A0A] text-white" data-testid="videos-page">
      <header className="border-b border-white/10 px-5 md:px-12 py-7 flex items-center justify-between">
        <img src={LOGO_WHITE} alt="Institut Impact Élite Martial" className="h-12 object-contain" data-testid="public-master-logo" />
        <div className="flex items-center gap-5">
          {!isMember && <Link to="/membres" className="kz-mono text-[10px] tracking-[0.2em] uppercase text-[#D90429] hover:text-white transition-colors" data-testid="login-link">Connexion membre</Link>}
          <Link to="/univers" className="kz-mono text-[10px] tracking-[0.2em] uppercase text-white/40 hover:text-white transition-colors flex items-center gap-2" data-testid="back-link">
            <ArrowLeft size={14} /> Retour
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 md:px-12 py-12">
        <p className="kz-mono text-[10px] tracking-[0.26em] uppercase text-[#D90429]">Institut Impact Élite Martial</p>
        <h1 className="kz-display text-4xl md:text-6xl font-medium mt-3">Vidéos techniques</h1>

        {/* Bandeau gamification membre */}
        {isMember && progress && (
          <div className="mt-6 flex flex-wrap items-center gap-4 border border-white/10 bg-white/[0.02] p-4" data-testid="member-progress">
            <span className="flex items-center gap-2 text-amber-300"><Coins size={20} weight="fill" /><b>{progress.credits}</b> crédits</span>
            <span className="flex items-center gap-2 text-white/70"><CheckCircle size={18} className="text-emerald-400" />{progress.watched_count} complétée(s)</span>
            <span className="flex items-center gap-2 text-white/70"><Trophy size={18} className="text-amber-400" />{(progress.badges || []).length} badge(s)</span>
            {isPremium && <span className="flex items-center gap-1.5 text-amber-300 text-xs uppercase tracking-wide"><Crown size={14} weight="fill" /> Premium</span>}
            {(progress.badges || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 w-full mt-1" data-testid="badges">
                {progress.badges.map((b) => <span key={b} className="text-[10px] uppercase tracking-wide bg-amber-500/15 text-amber-300 border border-amber-500/30 px-2 py-0.5">{b}</span>)}
              </div>
            )}
          </div>
        )}

        {/* CTA abonnement Premium (membre non-premium) */}
        {isMember && !isPremium && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-4 border border-amber-500/30 bg-amber-500/[0.06] p-5" data-testid="premium-cta">
            <div className="flex items-start gap-3">
              <Crown size={26} weight="fill" className="text-amber-300 shrink-0 mt-0.5" />
              <div>
                <h3 className="kz-display text-lg text-amber-100">Passe au Premium</h3>
                <p className="kz-body text-sm text-white/60 mt-0.5">Débloque toutes les capsules avancées · abonnement renouvelé aux 4 semaines, annulable à tout moment.</p>
              </div>
            </div>
            <button onClick={goPremium} data-testid="go-premium-btn"
              className="bg-amber-400 hover:bg-amber-300 text-black px-6 py-3 font-bold rounded flex items-center gap-2 shrink-0">
              <Crown size={18} weight="fill" /> Devenir Premium
            </button>
          </div>
        )}

        {/* Filtres discipline */}
        {disciplines.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-2" data-testid="discipline-filters">
            <FilterChip active={discipline === ""} onClick={() => setDiscipline("")}>Toutes</FilterChip>
            {disciplines.map((d) => <FilterChip key={d} active={discipline === d} onClick={() => setDiscipline(d)}>{d}</FilterChip>)}
          </div>
        )}

        {/* Barre de recherche technique */}
        <div className="mt-4 relative max-w-md" data-testid="public-video-search">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="public-video-search-input"
            placeholder="Rechercher une technique…"
            className="w-full bg-white/5 border border-white/10 rounded-full pl-9 pr-9 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-white/30"
          />
          {search && <button onClick={() => setSearch("")} data-testid="public-video-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">✕</button>}
        </div>

        {/* Onglets par volet : Techniques · Enchaînements · Training */}
        {(comboCount > 0 || trainingCount > 0) && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="video-type-tabs">
            <FilterChip active={videoType === "all"} onClick={() => setVideoType("all")}>Tout ({videos.length})</FilterChip>
            <FilterChip active={videoType === "technique"} onClick={() => setVideoType("technique")}>Techniques ({techniqueCount})</FilterChip>
            {comboCount > 0 && <FilterChip active={videoType === "combo"} onClick={() => setVideoType("combo")}>⚡ Enchaînements ({comboCount})</FilterChip>}
            {trainingCount > 0 && <FilterChip active={videoType === "training"} onClick={() => setVideoType("training")}>🎬 Training ({trainingCount})</FilterChip>}
          </div>
        )}

        {/* Filtre durée « J'ai … minutes » — volet Training uniquement */}
        {videoType === "training" && trainingCount > 0 && (
          <div className="mt-3" data-testid="training-duration-filter">
            <div className="text-[11px] uppercase tracking-widest text-sky-300/80 mb-1.5">🎬 J'ai … minutes — choisis ta séance à suivre</div>
            <div className="flex flex-wrap gap-2">
              <FilterChip active={durationBucket === "all"} onClick={() => setDurationBucket("all")}>Toutes</FilterChip>
              <FilterChip active={durationBucket === "10"} onClick={() => setDurationBucket("10")}>≤ 10 min</FilterChip>
              <FilterChip active={durationBucket === "15"} onClick={() => setDurationBucket("15")}>≤ 15 min</FilterChip>
              <FilterChip active={durationBucket === "30"} onClick={() => setDurationBucket("30")}>≤ 30 min</FilterChip>
              <FilterChip active={durationBucket === "45"} onClick={() => setDurationBucket("45")}>≤ 45 min</FilterChip>
              <FilterChip active={durationBucket === "60"} onClick={() => setDurationBucket("60")}>60 min +</FilterChip>
            </div>
          </div>
        )}

        {/* Forfait Combinaisons — débloquer le volet payant */}
        {comboCount > 0 && isMember && !data?.has_combo_pack && (
          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap rounded-lg border border-amber-400/30 bg-amber-400/[0.06] px-4 py-3" data-testid="combo-pack-banner">
            <span className="text-sm text-amber-200">⚡ Débloque les vidéos d'<b>Enchaînements</b> (volet Combinaisons) — accès à vie.</span>
            <button onClick={goComboPack} data-testid="combo-pack-btn" className="px-4 py-2 rounded-lg bg-amber-400 hover:bg-amber-300 text-black text-sm font-bold active:scale-[0.98] transition">Débloquer — 10 $</button>
          </div>
        )}

        {/* Bascule d'affichage : par ceinture (collections) ou grille complète */}
        {shownVideos.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="view-mode-tabs">
            <FilterChip active={groupByBelt} onClick={() => setGroupByBelt(true)}>🥋 Par ceinture</FilterChip>
            <FilterChip active={!groupByBelt} onClick={() => setGroupByBelt(false)}>Grille complète</FilterChip>
            <span className="w-px bg-white/10 mx-1 self-stretch" />
            <FilterChip active={sortBy === "default"} onClick={() => setSortBy("default")}>Ordre du cours</FilterChip>
            <FilterChip active={sortBy === "popular"} onClick={() => setSortBy("popular")}>🔥 Populaires</FilterChip>
          </div>
        )}

        {/* Grille */}
        {!data ? <p className="text-white/40 mt-10">Chargement…</p> : shownVideos.length === 0 ? (
          <p className="text-white/50 mt-10" data-testid="no-videos">Aucune capsule pour l'instant.</p>
        ) : groupByBelt ? (
          <div className="mt-8 space-y-10" data-testid="belt-collections">
            {beltGroups.map(([belt, vids]) => (
              <section key={belt} data-testid={`belt-section-${belt.replace(/\s+/g, "-")}`}>
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="kz-display text-xl md:text-2xl text-amber-100">{belt}</h2>
                  <span className="kz-mono text-[10px] uppercase tracking-widest text-white/40">{vids.length} capsule(s)</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {vids.map(renderCard)}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {shownVideos.map(renderCard)}
          </div>
        )}
      </div>

      {/* Lecteur */}
      {playing && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setPlaying(null)} data-testid="player-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="kz-display text-xl">{playing.title}</h3>
              <button onClick={() => setPlaying(null)} data-testid="close-player" className="text-white/60 hover:text-white"><X size={24} /></button>
            </div>
            <div className="aspect-video bg-black border border-white/10">
              {["vimeo", "bunny"].includes(playing.platform) && playing.embed_url ? (
                <iframe title={playing.title} src={playing.embed_url} className="w-full h-full" allow="autoplay; fullscreen; picture-in-picture; encrypted-media" allowFullScreen data-testid="player-iframe" />
              ) : playing.platform === "direct" && playing.video_url ? (
                <video src={playing.video_url} controls className="w-full h-full" data-testid="player-video" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-white/60">
                  <Play size={40} />
                  <a href={playing.video_url} target="_blank" rel="noreferrer" className="text-[#D90429] underline kz-mono text-xs uppercase tracking-widest">Ouvrir la vidéo ({playing.platform}) →</a>
                </div>
              )}
            </div>
            {isMember && (
              <div className="flex items-center justify-between mt-4">
                <span className="text-white/50 text-sm">{playing.watched ? "Déjà complétée ✓" : `Complète pour gagner ${playing.credits_reward} crédits`}</span>
                <button onClick={() => markWatched(playing)} disabled={playing.watched} data-testid="mark-watched-btn"
                  className="bg-[#D90429] hover:bg-[#b8031f] disabled:opacity-40 text-white px-5 py-2.5 font-semibold rounded flex items-center gap-2">
                  <CheckCircle size={18} weight="fill" /> {playing.watched ? "Complétée" : "Marquer comme complétée"}
                </button>
              </div>
            )}
            {!isMember && (
              <p className="text-center text-white/50 text-sm mt-4">
                <Link to="/membres" className="text-[#D90429] underline">Connecte-toi en tant que membre</Link> pour gagner des crédits et débloquer le contenu premium.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`text-xs px-3 py-1.5 border transition-colors ${active ? "bg-[#D90429] border-[#D90429] text-white" : "border-white/15 text-white/60 hover:border-white/40"}`}>{children}</button>
  );
}
